/**
 * Drag-and-drop plumbing that is worth testing without a pointer.
 *
 * The gesture itself is `@dnd-kit/core` (the repo's established choice — see
 * `components/Kanban`), used for sensors, activation constraints and the drag
 * lifecycle. Its *collision detection* is deliberately not used: where a block
 * lands is decided by `dropTargetAtPoint` against measured rects from the canvas
 * iframe, which is geometry dnd-kit cannot see. So the only thing the two need to
 * agree on is what is being dragged, which is what this module encodes.
 */
import { Point } from './geometry';

export const PALETTE_DRAG_PREFIX = 'cvn-palette:';
export const BLOCK_DRAG_PREFIX = 'cvn-block:';

export type DragSubject =
  /** A new block of `type`, from the palette. */
  | { kind: 'palette'; type: string }
  /** An existing block being moved. */
  | { kind: 'block'; blockId: string };

/**
 * What a dnd-kit draggable id refers to.
 *
 * Parsed from the id rather than trusted from `active.data`, so a drag started by
 * anything else on the page (the Kanban board, a sortable list in another drawer)
 * is ignored instead of being interpreted as an email block.
 */
export function parseDragId(id: unknown): DragSubject | null {
  if (typeof id !== 'string') return null;

  if (id.startsWith(PALETTE_DRAG_PREFIX)) {
    const type = id.slice(PALETTE_DRAG_PREFIX.length);
    return type ? { kind: 'palette', type } : null;
  }

  if (id.startsWith(BLOCK_DRAG_PREFIX)) {
    const blockId = id.slice(BLOCK_DRAG_PREFIX.length);
    return blockId ? { kind: 'block', blockId } : null;
  }

  return null;
}

/** The minimal shape of the events this reads, so tests need no DOM. */
export interface DragActivator {
  clientX?: number;
  clientY?: number;
}

/**
 * The pointer's current viewport position during a drag.
 *
 * dnd-kit reports the *activator* event plus a running delta rather than live
 * coordinates, so the position has to be reconstructed. Returns null for a
 * non-pointer activator — a keyboard drag has no coordinates, and guessing some
 * would drop the block somewhere the user never aimed.
 */
export function dragPointFrom(
  activatorEvent: DragActivator | null | undefined,
  delta: { x: number; y: number } | null | undefined,
): Point | null {
  const x = activatorEvent?.clientX;
  const y = activatorEvent?.clientY;
  if (typeof x !== 'number' || typeof y !== 'number') return null;

  return { x: x + (delta?.x ?? 0), y: y + (delta?.y ?? 0) };
}
